import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { StableInput, StableTextarea } from '../components/StableInput';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Clock,
  DollarSign,
  CheckCircle,
  Loader2,
  MessageSquare,
  Phone,
  Users,
  Target,
  ArrowRight,
  Sparkles,
  Calendar,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SESSION_BENEFITS = [
  { icon: Target, text: 'Personalized fundraising strategy' },
  { icon: Users, text: 'Expert guidance from seasoned consultants' },
  { icon: MessageSquare, text: 'Actionable next steps for your org' },
  { icon: Calendar, text: 'Flexible scheduling after booking' },
];

export default function Consulting() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const bookingId = searchParams.get('booking');

  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    organization: '',
    topic: '',
  });

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    if (status === 'success' && bookingId) {
      confirmBooking(bookingId);
    }
  }, [status, bookingId]);

  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        name: prev.name || user.organization_name || '',
        email: prev.email || user.email || '',
      }));
    }
  }, [user]);

  useEffect(() => {
    if (status === 'cancelled') {
      toast.info('Checkout was cancelled');
    }
  }, [status]);

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API}/api/consulting/sessions`);
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
        if (data.sessions?.length > 0) setSelectedSession(data.sessions[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    }
  };

  const confirmBooking = async (id) => {
    setConfirming(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/consulting/confirm/${id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        setConfirmed(true);
        toast.success('Booking confirmed! Check your email for next steps.');
      }
    } catch (err) {
      console.error('Confirmation error:', err);
    } finally {
      setConfirming(false);
    }
  };

  const handleCheckout = async () => {
    if (!selectedSession || !form.name.trim() || !form.email.trim() || !form.topic.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!user) {
      toast.error('Please log in to book a session');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/consulting/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_type: selectedSession,
          name: form.name,
          email: form.email,
          organization: form.organization,
          topic: form.topic,
          origin_url: window.location.origin,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        window.location.href = data.checkout_url;
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to start checkout');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Confirmation screen
  if (confirmed || (status === 'success' && confirming)) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] pt-24 pb-16" data-testid="consulting-page">
        <div className="max-w-lg mx-auto px-4 text-center">
          {confirming ? (
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          ) : (
            <>
              <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-[#0A0A0A] mb-3">Session Booked!</h1>
              <p className="text-[#5C6B67] mb-6">
                Thank you for booking a strategy session. Our team at Strategic Fundraising Consulting LLC will reach out within 24 hours to schedule your session.
              </p>
              <p className="text-sm text-[#5C6B67]">
                Questions? Contact us at{' '}
                <a href="mailto:info@strategicfundraisingconsultingllc.com" className="text-primary hover:underline">
                  info@strategicfundraisingconsultingllc.com
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  const currentSession = sessions.find(s => s.id === selectedSession);

  return (
    <div className="min-h-screen bg-[#FAFAFA] pt-20 pb-16" data-testid="consulting-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-gradient-to-br from-primary to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg">
            <Phone className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A0A0A] mb-3">
            Book a Strategy Session
          </h1>
          <p className="text-[#5C6B67] max-w-xl mx-auto text-base">
            Get expert fundraising guidance from Strategic Fundraising Consulting LLC. Structured, focused, and built for nonprofits.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left – Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Session options */}
            <div className="space-y-3">
              <h3 className="font-semibold text-stone-800 text-sm uppercase tracking-wide">Choose Your Session</h3>
              {sessions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSelectedSession(s.id)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    selectedSession === s.id
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-[#E2E8E6] bg-white hover:border-stone-300'
                  }`}
                  data-testid={`session-option-${s.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-stone-900">{s.name}</span>
                    <Badge className={selectedSession === s.id ? 'bg-primary text-white' : 'bg-stone-100 text-stone-700'}>
                      ${s.price}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-[#5C6B67]">
                    <Clock className="w-3.5 h-3.5" />
                    {s.duration}
                  </div>
                </button>
              ))}
            </div>

            {/* Benefits */}
            <Card className="bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-stone-800 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  What You Get
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="space-y-3">
                  {SESSION_BENEFITS.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#5C6B67]">
                      <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <b.icon className="w-3.5 h-3.5 text-primary" />
                      </div>
                      {b.text}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Right – Form */}
          <div className="lg:col-span-3">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-[#0A0A0A]">Your Details</CardTitle>
                <CardDescription>
                  Tell us about yourself and what you'd like to discuss so we can prepare.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Your Name *</Label>
                    <StableInput
                      value={form.name}
                      onChange={(f, v) => setForm(prev => ({ ...prev, [f]: v }))}
                      field="name"
                      placeholder="Jane Smith"
                      className="mt-1"
                      data-testid="consulting-name"
                    />
                  </div>
                  <div>
                    <Label>Email Address *</Label>
                    <StableInput
                      value={form.email}
                      onChange={(f, v) => setForm(prev => ({ ...prev, [f]: v }))}
                      field="email"
                      type="email"
                      placeholder="jane@nonprofit.org"
                      className="mt-1"
                      data-testid="consulting-email"
                    />
                  </div>
                </div>

                <div>
                  <Label>Organization</Label>
                  <StableInput
                    value={form.organization}
                    onChange={(f, v) => setForm(prev => ({ ...prev, [f]: v }))}
                    field="organization"
                    placeholder="Your nonprofit name"
                    className="mt-1"
                    data-testid="consulting-organization"
                  />
                </div>

                <div>
                  <Label>What would you like to discuss? *</Label>
                  <StableTextarea
                    value={form.topic}
                    onChange={(f, v) => setForm(prev => ({ ...prev, [f]: v }))}
                    field="topic"
                    placeholder="E.g. Grant writing strategy, donor cultivation plan, board engagement, event fundraising..."
                    rows={5}
                    className="mt-1"
                    data-testid="consulting-topic"
                  />
                </div>

                {currentSession && (
                  <div className="bg-[#FAFAFA] rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-stone-900">{currentSession.name}</p>
                      <p className="text-sm text-[#5C6B67]">{currentSession.duration}</p>
                    </div>
                    <p className="text-2xl font-bold text-primary">${currentSession.price}</p>
                  </div>
                )}

                <Button
                  className="w-full h-12 bg-primary hover:bg-primary-800 text-white text-base"
                  onClick={handleCheckout}
                  disabled={loading || !selectedSession || !form.name.trim() || !form.email.trim() || !form.topic.trim()}
                  data-testid="consulting-checkout-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="w-5 h-5 mr-2" />
                  )}
                  {loading ? 'Redirecting to Checkout...' : `Pay $${currentSession?.price || '...'} & Book Session`}
                </Button>

                <p className="text-xs text-stone-400 text-center">
                  Secure payment via Stripe. After payment, we'll email you to schedule.
                  {'\n'}Sessions are structured and focused. Guided plan members get 1 included per month.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
